import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import heroBackground from '../../assets/images/hero.png';

function Hero() {
  // Memoize the particles array to prevent unnecessary re-renders
  const particles = useMemo(() => 
    Array.from({ length: 15 }, (_, i) => ({
      key: i,
      size: i % 2 === 0 ? 'w-1 h-1' : 'w-2 h-2',
      color: i % 3 === 0 ? 'bg-[#C334F6]/30' : 'bg-[#543DF4]/30',
      initialX: Math.random() * window.innerWidth,
      initialY: Math.random() * window.innerHeight,
      scale: Math.random() * 2 + 1,
      duration: Math.random() * 4 + 3,
      yOffset: Math.random() * 200 - 100
    }))
  , []);

  return (
    <section className="bg-black text-white min-h-[90vh] relative overflow-hidden">
      {/* Background with optimized gradient overlays */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/10 to-[#543DF4]/10" />
      </div>

      {/* Optimized floating elements */}
      <div className="absolute left-0 top-1/4 w-1/4 h-1/2 z-10">
        <div className="relative h-full">
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-[#C334F6]/20 to-[#543DF4]/20 rounded-full blur-2xl animate-float" />
          <div className="absolute top-1/4 left-1/4 w-24 h-24 bg-gradient-to-br from-[#543DF4]/20 to-[#C334F6]/20 rounded-full blur-xl animate-float-reverse" />
        </div>
      </div>

      <div className="absolute right-0 top-1/4 w-1/4 h-1/2 z-10">
        <div className="relative h-full">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#543DF4]/20 to-[#C334F6]/20 rounded-full blur-2xl animate-float-reverse" />
          <div className="absolute top-1/4 right-1/4 w-24 h-24 bg-gradient-to-br from-[#C334F6]/20 to-[#543DF4]/20 rounded-full blur-xl animate-float" />
        </div>
      </div>

      {/* Optimized particles */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {particles.map((particle) => (
          <motion.div
            key={particle.key}
            className={`absolute ${particle.size} ${particle.color} rounded-full`}
            initial={{
              x: particle.initialX,
              y: particle.initialY,
              scale: particle.scale,
            }}
            animate={{
              y: [null, particle.yOffset],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 pt-32 pb-40 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto text-center"
        >
          <div className="inline-block mb-8 px-6 py-3 rounded-full bg-gradient-to-r from-[#C334F6]/20 to-[#543DF4]/20 border border-[#C334F6]/30 backdrop-blur-sm">
            <span className="text-[#C334F6] font-medium text-lg tracking-wider">✨ NEXT-GEN LEAD GENERATION</span>
          </div>

          <h1 className="font-['Neue_Montreal'] text-6xl md:text-9xl font-medium leading-[1.1] mb-12 tracking-tight">
            <span className="block opacity-90">Transform Your</span>
            <span className="relative inline-block mt-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C334F6] to-[#543DF4]">
                Lead Generation
              </span>
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#C334F6] to-[#543DF4]" />
            </span>
            <span className="block mt-4 opacity-90">Game Today</span>
          </h1>

          <p className="font-['Neue_Montreal'] text-2xl md:text-3xl text-gray-300 leading-relaxed mb-16 max-w-4xl mx-auto tracking-wide">
            Harness the power of AI-driven lead generation. Automate your outreach,
            nurture relationships, and close more deals with our cutting-edge platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-24">
            <button className="relative group bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white px-12 py-6 rounded-full text-xl font-medium inline-flex items-center overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <span className="relative z-10 flex items-center">
                Start Free Trial
                <span className="ml-4 bg-white rounded-full p-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </span>
            </button>

            <button className="relative group text-white px-12 py-6 rounded-full text-xl font-medium border-2 border-white/20 hover:border-white/40 transition-all duration-300">
              <span className="relative z-10">Schedule Demo</span>
            </button>
          </div>

          {/* Stats section */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-12">
            {[
              { number: '10K+', label: 'Active Users', icon: '👥' },
              { number: '50K+', label: 'Leads Generated', icon: '📈' },
              { number: '95%', label: 'Success Rate', icon: '🎯' },
              { number: '24/7', label: 'Support', icon: '🛡️' }
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-3xl bg-gradient-to-b from-white/5 to-white/0 border border-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-sm"
              >
                <div className="text-5xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#C334F6] to-[#543DF4] mb-2">
                  {stat.number}
                </div>
                <div className="text-base text-gray-400 tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-24">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[#C334F6] text-sm tracking-wider mb-8 font-medium"
            >
              TRUSTED BY LEADING COMPANIES
            </motion.p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {['Google', 'Microsoft', 'Amazon', 'Meta', 'Apple'].map((company, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="px-6 py-3 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 group-hover:border-[#C334F6]/30 transition-all duration-300">
                    <span className="text-white/80 group-hover:text-white text-lg font-medium tracking-wide">
                      {company}
                    </span>
                  </div>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#C334F6]/20 to-[#543DF4]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default React.memo(Hero);